import { BannerSlideProps } from "@omni/types/components/props/bannerSlider.props";
import { DelayValues } from "@omni/types/components/props/shared/delay.values";
import { SpeedValues } from "@omni/types/components/props/shared/speed.values";
import { TrackingProps } from "@omni/types/components/tracking/trackingProps.type";
import { RequireChildrenFor } from "@omni/types/components/utils/children.types";
import { isDefined } from "@omni/utils/helper.utils";
import { createContext, use, useCallback, useState } from "react";
import { Swiper as SwiperCore } from "swiper/types";

const INTERVAL_MS = 50;

type BannerSliderContextValue = {
    trackingProps: TrackingProps;
    delay: DelayValues; // in milliseconds
    speed: SpeedValues; // in milliseconds
    slides: Array<BannerSlideProps>;
    setMainSwiper: React.Dispatch<React.SetStateAction<SwiperCore | undefined>>;
    interval: number;
    slideTo: (index: number) => void;
    slideNext: () => void;
    activeIndex: number;
    setActiveIndex: React.Dispatch<React.SetStateAction<number>>;
};

type BannerSliderContextProviderProps = RequireChildrenFor<{
    trackingProps: TrackingProps;
    delay: DelayValues; // in milliseconds
    speed: SpeedValues; // in milliseconds
    slides: Array<BannerSlideProps>;
}>;

export const BannerSliderContext = createContext<
    BannerSliderContextValue | undefined
>(undefined);
BannerSliderContext.displayName = "BannerSliderContext";

export const BannerSliderContextProvider = ({
    trackingProps,
    delay,
    speed,
    slides,
    children
}: BannerSliderContextProviderProps) => {
    const [mainSwiper, setMainSwiper] = useState<SwiperCore>();
    const [activeIndex, setActiveIndex] = useState(0);

    const slideTo = useCallback(
        (index: number) => {
            if (mainSwiper) {
                mainSwiper.slideTo(index);
            }
        },
        [mainSwiper]
    );

    const slideNext = useCallback(() => {
        if (mainSwiper) {
            mainSwiper.slideNext();
        }
    }, [mainSwiper]);

    return (
        <BannerSliderContext
            value={{
                trackingProps,
                delay,
                speed,
                interval: INTERVAL_MS,
                slides,
                slideTo,
                slideNext,
                activeIndex,
                setActiveIndex,
                setMainSwiper
            }}
        >
            {children}
        </BannerSliderContext>
    );
};

export const useBannerSliderContext = () => {
    const context = use<BannerSliderContextValue | undefined>(
        BannerSliderContext
    );

    if (!isDefined(context)) {
        throw new Error(`${BannerSliderContext.displayName} is undefined.`);
    }

    return context;
};
